<template>
  <transition name="slide-fade">
    <div class="job-edit-modal" v-if="job">
      <div class="title-bar top" style="flex-direction: column">
        <div style="display: flex; width: 100%; justify-content: space-between">
          <h4>
            {{ job.order.vend_invoice_number }} —
            {{ getCustomerName(job.order.customer) }}
          </h4>
          <a class="close-modal" @click="hideJobEditModal"><i class="di-remove-10"></i></a>
        </div>
        <div class="name-bar">
          <div>
            <h2 style="text-align: left">{{ job.productName }}</h2>
            <div class="description">{{ job.productOptions }}</div>
          </div>
          <div class="price">${{ price }}</div>
        </div>
      </div>
      <div class="modal-wrapper" style="margin-top: 85px">
        <Frame :customer="job.order.customer"></Frame>
        <template v-if="isPrescriptionRequired">
          <Prescription
            :customer="job.order.customer"
            :productType="job.item.region_product.product.type"
            :lensType="lensType"
            @show-prescription-modal="onShowPrescriptionModal"
            @show-prescription-edit-modal="onShowPrescriptionEditModal"
            @show-prescription-copy-modal="onShowPrescriptionCopyModal"
            @prescriptionDataSent="setPrescriptionData"
            @customerPd="customerPd"
            @show-pd-edit-modal="onShowPdEditModal"
            :lensesToPick="lensesToPick"
          ></Prescription>
          <PupilDistance
            :customerPds="customerPds"
            :lenses="job.lenses"
            :additionalInfo="job.item.additional_info"
            :customer="job.order.customer"
            :item="job.item"
            :lensType="lensType"
            @show-pd-modal="onShowPdModal"
            @show-pd-edit-modal="onShowPdEditModal"
            @show-pd-copy-modal="onShowPdCopyModal"
            @pdDataSent="setPdData"
          ></PupilDistance>
          <Lenses
            :customer="job.order.customer"
            :prescription="prescriptionData"
            :orderLens="job.item"
            @lensDataSent="setLensData"
          ></Lenses>
        </template>
        <NewPrescriptionModal
          :customer="job.order.customer"
          :prescription="editingPrescription"
          :isOnPageEdit="isOnPageEdit"
          :isCopy="isCopy"
          @onShowPdEditModal="onShowPdEditModal"
          @prescriptionDataSent="setPrescriptionData"
        ></NewPrescriptionModal>
        <NewPdModal
          :customer="job.order.customer"
          :isOnPageEdit="isOnPageEdit"
          :pd="editingPd"
          :isCopy="isCopy"
          :lensType="lensType"
        ></NewPdModal>
        <div class="footer-bar" v-if="showSaveButton">
          <b-button class="mr-3" variant="outline-secondary" @click="hideJobEditModal" type="button"
            >Cancel</b-button
          >
          <b-button
            class="button-with-loader"
            variant="primary"
            v-wait:disabled="'Job Edit'"
            type="button"
            @click="postJobEditForm"
          >
            <v-wait class="d-inline-block mr-2" for="Job Edit"
              ><template slot="waiting">
                <div>
                  <b-spinner small="small"></b-spinner>
                </div> </template></v-wait
            >Save and close
          </b-button>
        </div>
      </div>
    </div>
  </transition>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import apiOrders from '@/api/orders';
import apiLenses from '@/api/lenses';
import apiPds from '@/api/pds';
import Frame from './steps/Frame.vue';
import Prescription from './steps/Prescription.vue';
import PupilDistance from './steps/PupilDistance.vue';
import Lenses from './steps/Lenses.vue';
import NewPrescriptionModal from '../NewPrescriptionModal.vue';
import NewPdModal from '../NewPdModal.vue';
export default {
  name: 'JobEditModal',
  data() {
    return {
      frameData: null,
      prescriptionData: null,
      prescriptionType: null,
      pdData: null,
      lensData: null,
      selectedCustomer: {},
      customerPds: null,
      lensType: null,
      editingPd: null,
      editingPrescription: null,
      isOnPageEdit: false,
      isCopy: false,
    };
  },
  components: {
    Frame,
    Prescription,
    NewPrescriptionModal,
    NewPdModal,
    PupilDistance,
    Lenses,
  },
  computed: {
    ...mapState({
      job: state => state.jobsModule.selectedJobData,
      activeJobEditStep: state => state.jobsModule.activeJobEditStep,
      jobs: state => state.jobs,
      selectedFrameData: state => state.jobsModule.selectedFrameData,
      selectedLens: state => state.jobsModule.selectedLens,
      selectedPrescription: state => state.jobsModule.selectedPrescription,
      selectedPrescriptionType: state => state.jobsModule.selectedPrescriptionType,
      selectedPd: state => state.jobsModule.selectedPd,
    }),
    ...mapGetters('jobsModule', ['isPrescriptionRequired', 'selectedPrescriptionType']),
    price() {
      if (this.job.item && typeof this.job.item.historical_unit_price !== 'undefined') {
        return this.job.item.historical_unit_price;
      }
      return null;
    },
    lensesToPick() {
      return this._.get(this.job, 'item.additional_info.lensesToPick');
    },
    selectedNewFrameHasData() {
      return (
        this.selectedFrameData.frameSize &&
        this.selectedFrameData.leftArmSize &&
        this.selectedFrameData.rightArmSize
      );
    },
    isSingleVision() {
      return this.job.item.region_product.product.type.lenses_type === 'single-vision';
    },
    isPrescriptionTypeValid() {
      if (!this.isSingleVision) return true;
      // DP-5685: Intermediate needs an Int Add, also when the item already has a lens saved
      const rx = this.selectedPrescription || {};
      if (this.selectedPrescriptionType === 'intermediate') return !!(rx.add_int || (rx.right_int_add && rx.left_int_add));
      return !!this.selectedPrescriptionType;
    },
    showSaveButton() {
      return (
        (this.activeJobEditStep === 5 &&
          this.selectedNewFrameHasData &&
          (!this.isPrescriptionRequired ||
            (this.isPrescriptionTypeValid &&
              !this._.isEmpty(this.selectedLens) &&
              !this._.isEmpty(this.selectedPrescription) &&
              !this._.isEmpty(this.selectedPd) &&
              !this._.get(this.selectedPd, 'pdPresenter.error')))) ||
        !this.isPrescriptionRequired
      );
    },
  },
  mounted() {
    this.$root.$on('newPdAdded', this.customerPd);

    if (this.job.frame) {
      this.prefillEditedJobData();
    }
    if (this.job.item) {
      apiLenses
        .getLensType(this.job.item.region_product_id)
        .then(({ data: lensType }) => {
          this.lensType = lensType;
          if (this.job.order.customer?.id) this.getPds();
          else this.checkGuestPd();
        })
        .catch(({ response }) => {
          this.$store.dispatch('showErrorAlert', response.data.error);
        });
    }
  },
  beforeDestroy() {
    // DP-5111: the modal is v-if'd, so a listener left behind would reload PDs for an old job and could
    // overwrite the selected PD now that Create PD sends the new PD id
    this.$root.$off('newPdAdded', this.customerPd);
  },
  methods: {
    customerPd(pd = null) {
      if (pd) {
        this.getPds(pd.selectedId);
      } else {
        this.getPds();
      }
    },
    hideJobEditModal() {
      this.$store.commit('jobsModule/HIDE_JOB_EDIT_MODAL');
    },
    setPrescriptionData(data) {
      this.prescriptionData = data.prescription;
      this.prescriptionType = data.prescriptionType;
    },
    setPdData(data) {
      this.pdData = data;
    },
    setLensData(data) {
      this.lensData = data;
    },
    prefillEditedJobData() {
      this.$store.commit('jobsModule/SET_JOB_EDIT_MODAL_DATA', this.job);
    },
    updateJob(job, updateData) {
      if (job.item.additional_info) {
        return apiOrders.updateOrderItemAdditionalInfo(
          this.job.item.id,
          this.job.item.additional_info.id,
          updateData,
        );
      }
      return apiOrders.createOrderItemAdditionalInfo(this.job.item.id, updateData);
    },
    postJobEditForm() {
      this.$wait.start('Job Edit');

      let warning_message = null;
      if (this._.get(this.selectedLens, 'lenses.warning_message')) {
        warning_message = this._.get(this.selectedLens, 'lenses.warning_message');
      }

      const updateData = {
        customize: {
          right: {
            size: this.selectedFrameData.rightArmSize,
            colorId: this.selectedFrameData.rightArmColor,
            existingdresden: this.selectedFrameData.isExistingDresden,
          },
          left: {
            size: this.selectedFrameData.leftArmSize,
            colorId: this.selectedFrameData.leftArmColor,
            existingdresden: this.selectedFrameData.isExistingDresden,
          },
          front: {
            size: this.selectedFrameData.frameSize,
            colorId: this.selectedFrameData.frameColor,
            existingdresden: this.selectedFrameData.isExistingDresden,
            shape: this.selectedFrameData.shape,
          },
        },
        ...(this.isPrescriptionRequired && {
          prescription: {
            type: this.selectedPrescriptionType ? this.selectedPrescriptionType : null,
            id: this.selectedPrescription.id,
          },
          pdId: this.selectedPd.detail.id,
          product_lens_addons_id: this.selectedLens.id,
          product_upgrade_reason: this.selectedLens.upgrade_reason || null,
          from_region_product_id: this.selectedLens.from_region_product_id || null,
          lens_selected_mod: this.selectedLens.selected_lens_mod ? this.selectedLens.selected_lens_mod.id : null,
          lens_selected_mod_corridor: this.selectedLens.selected_lens_mod_corridor ? this.selectedLens.selected_lens_mod_corridor.id : null,
          lens_warning_message: warning_message,
        }),
      };

      // Check if this is a Dresden Go item that needs DresdenGo_reading data refetch
      const hasDresdenGoReading = !!this._.get(this.job.item, 'DresdenGo_reading.prescription');
      const orderId = this.job.item.order_id;

      this.updateJob(this.job, updateData, this.selectedFrameData.isExistingDresden)
        .then(response => {
          const updatedJob = this._.find(this.jobs, ['id', this.job.item.order_id]);
          const relatedGroupItem = this._.find(updatedJob.item_groups, [
            'id',
            response.data.order_item.group_id,
          ]);
          const relatedOrderItem = this._.find(relatedGroupItem.order_items, [
            'id',
            response.data.order_item_id,
          ]);
          relatedOrderItem.additional_info = { ...response.data };
          if (this.isPrescriptionRequired && relatedOrderItem.additional_info.data.pd) {
            relatedOrderItem.additional_info.data.pd.pdPresenter = response.data.pdPresenter;
          }

          // If this is a Dresden Go item, refetch order details to get updated DresdenGo_reading data
          if (hasDresdenGoReading) {
            return apiOrders.getOrder(orderId).then(orderResponse => {
              // Find the updated order item with DresdenGo_reading data
              const updatedOrderData = orderResponse.data.data;
              let updatedItemWithDresdenReading = null;

              for (const group of updatedOrderData.item_groups) {
                const foundItem = group.order_items.find(
                  item => item.id === response.data.order_item_id,
                );
                if (foundItem) {
                  updatedItemWithDresdenReading = foundItem;
                  break;
                }
              }

              // Update DresdenGo_reading data in local state
              if (updatedItemWithDresdenReading && updatedItemWithDresdenReading.DresdenGo_reading) {
                relatedOrderItem.DresdenGo_reading = updatedItemWithDresdenReading.DresdenGo_reading;
              }

              return response;
            });
          }

          return response;
        })
        .then(() => {
          this.$store.dispatch('showSuccessAlert', 'Selections successfully confirmed.');
          this.$store.commit('UPDATE_JOB_IN_JOBS', this._.find(this.jobs, ['id', orderId]));
        })
        .catch(error => {
          this.$store.dispatch('showErrorAlert', this._.get(error.response, 'data.error', 'Error'));
        })
        .finally(() => {
          this.hideJobEditModal();
          this.$wait.end('Job Edit');
        });
    },
    // DP-5720: a guest PD is not listed, so it is checked on its own against this lens type
    checkGuestPd() {
      const id = this._.get(this.selectedPd, 'detail.id');
      if (!id || !this._.get(this.lensType, 'id')) return;
      apiPds
        .newGetPd(id, this.prescriptionType || 'distance', this.lensType.id)
        .then(({ data }) => {
          const pdPresenter = this._.get(data, 'data.pdPresenter');
          if (pdPresenter && this._.get(this.selectedPd, 'detail.id') === id) {
            this.$store.commit('jobsModule/SET_SELECTED_PD', { ...this.selectedPd, pdPresenter });
          }
        })
        .catch(() => {});
    },
    getPds(selectedId = null) {
      if (this.job.order.customer !== null) {
        this.$wait.start('Pd List');
        return apiPds
          .getCustomerPd(
            this.job.order.customer.id,
            this._.get(this.lensType, 'id'),
            this.prescriptionType,
            this.selectedFrameData.shape,
          )
          .then(response => {
            this.customerPds = {
              pds: {
                data: response.data.data,
              },
            };

            if (selectedId) {
              const pdObj = response.data.data.filter(pd => pd.id === selectedId)[0];
              this.pdData = pdObj;

              this.$store.commit('jobsModule/SET_SELECTED_PD', {
                detail: pdObj,
                pdPresenter: pdObj.pdPresenter,
              });
            }
            // DP-5720: the PD prefilled from the item carries the presenter saved without a lens type; take
            // the one checked against this lens type, so the PD step and the lens list show its problem
            const current = this._.get(this.selectedPd, 'detail.id');
            const listed = !selectedId && current && response.data.data.find(pd => pd.id === current);
            if (listed && listed.pdPresenter) {
              this.$store.commit('jobsModule/SET_SELECTED_PD', {
                ...this.selectedPd,
                pdPresenter: listed.pdPresenter,
              });
            }
            
            // Force reactivity update for the customerPds prop
            this.$nextTick(() => {
              this.customerPds = { ...this.customerPds };
            });
          })
          .catch(({ response }) => {
            this.$store.dispatch('showErrorAlert', response.data.error);
          })
          .finally(() => {
            this.$wait.end('Pd List');
          });
      }
      return null;
    },
    isConfirmed(item) {
      if (item) {
        const isPrescriptionRequired = item.region_product.requires_prescription;
        if (isPrescriptionRequired) {
          return !!(
            item.additional_info.data.prescription &&
            item.additional_info.data.pd &&
            item.additional_info.data.lens_addon_combination.lenses
          );
        }
        return !!item.additional_info.data.customize?.component;
      }
      return false;
    },
    getGlass(items) {
      const glassesKey = ['glasses', 'lenses-only'];
      return items.find(item => {
        const typeKey = item.region_product.product.type.parent_type.key;
        return glassesKey.includes(typeKey);
      });
    },
    isAllSelectionsConfirmed() {
      return this.job.order.item_groups.every(g => {
        const glass = this.getGlass(g.order_items);
        return this.isConfirmed(glass);
      });
    },
    showPrintPrompt() {
      return this.$bvModal.msgBoxConfirm(
        'Now that all selections are confirmed for this order, would you like to print a job docket?',
        {
          title: 'Print job docket?',
          size: 'sm',
          okVariant: 'primary',
          okTitle: 'Print',
          okClass: 'test',
          cancelVariant: 'outline-secondary',
          cancelTitle: 'No thanks',
          hideHeaderClose: false,
          centered: true,
          headerClass: 'border-bottom p-3',
          bodyClass: 'p-4',
          footerClass: 'bg-white p-2 justify-content-center',
        },
      );
    },
    onShowPrescriptionModal() {
      this.isCopy = false;
      this.editingPrescription = null;
      this.$bvModal.show('new-prescription-modal');
    },
    onShowPrescriptionEditModal(e) {
      this.isCopy = false;
      this.editingPrescription = e.prescription;
      if (e.isOnPageEdit) {
        this.isOnPageEdit = true;
      }
      this.$bvModal.show('new-prescription-modal');
    },
    onShowPrescriptionCopyModal(e) {
      this.isCopy = true;
      this.editingPrescription = e.prescription;
      this.$bvModal.show('new-prescription-modal');
    },
    onShowPdModal() {
      this.isCopy = false;
      this.editingPd = null;
      this.$bvModal.show('new-pd-modal');
    },
    onShowPdEditModal(e) {
      this.isCopy = false;
      this.editingPd = e.pd;
      if (e.isOnPageEdit) {
        this.isOnPageEdit = true;
      }
      this.$bvModal.show('new-pd-modal');
    },
    onShowPdCopyModal(e) {
      this.isCopy = true;
      this.editingPd = e.pd;
      this.$bvModal.show('new-pd-modal');
    },
  },
};
</script>

<style>
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.5s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to
    /* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}
</style>
